import React, { useRef, useState, useEffect } from "react";
import Styles from "./CustomCarousel.module.scss";
import classnames from "classnames";
import { useIntersect } from "hooks/useIntersect";

interface CustomCarouselProps {
    children?: React.ReactNode;
    containerClass?: string;
    rowClass?: string;
    itemClass?: string;
    activeClass?: string;
    nextArrowClass?: string;
    prvArrowClass?: string;
    prevNextBtnsClass?: string;
    dotsClass?: string;
    activeDotClass?: string;
    scrollBy?: number;
    items?: React.ReactNode[];
    fallback?: React.ReactNode;
    prvArrow?: React.ReactNode;
    nextArrow?: React.ReactNode;
    length?: number;
}

const CustomCarousel = ({
    children,
    containerClass,
    rowClass,
    itemClass,
    activeClass,
    nextArrowClass,
    prvArrowClass,
    prevNextBtnsClass,
    scrollBy,
    items,
    fallback,
    prvArrow,
    nextArrow,
    length,
    dotsClass,
    activeDotClass
}: CustomCarouselProps) => {
    const elmRef = useRef<HTMLDivElement>(null!);
    const itemRef = useRef<HTMLDivElement>(null!);
    const [scrollByitemWidth, setScrollByitemWidth] = useState(0);
    const [isLastItem, setIsLastItem] = useState(false);
    const [isFirstItem, setIsFirstItem] = useState(false);
    const [currentItem, setCurrentItem] = useState(0);
    const [isMouseOver, setIsMouseOver] = useState(false);

    useEffect(() => {
        if (scrollByitemWidth) {
            scrollHorizontal(scrollByitemWidth);
        }
    }, [scrollByitemWidth, currentItem]);

    const { entry: lastInterSectionObj, setNode: lastElmRef } = useIntersect({
        // rootMargin: getHeightByViewPort(0),
        threshold: 1
    });
    useEffect(() => {
        if (lastInterSectionObj?.intersectionRatio === 1) {
            setIsLastItem(true);
        } else {
            setIsLastItem(false);
        }
        return () => {
            setIsLastItem(false);
        };
    }, [lastInterSectionObj, length]);

    const { entry: firstInterSectionObj, setNode: firstElmRef } = useIntersect({
        // rootMargin: getHeightByViewPort(0),
        threshold: 1
    });
    useEffect(() => {
        if (firstInterSectionObj?.intersectionRatio === 1) {
            setIsFirstItem(true);
        } else {
            // if (items?.length === 1) {
            //     setIsLastItem (false)
            // }
            setIsFirstItem(false);
        }
        return () => {
            setIsFirstItem(false);
        };
    }, [firstInterSectionObj, length]);

    const scrollHorizontal = (scrollOffset: number) => {
        elmRef.current.scrollLeft += scrollOffset;
        setScrollByitemWidth(0);
    };

    const handlePrvArrow = (event: any) => {
        if (isFirstItem) {
            setScrollByitemWidth(itemRef.current.offsetWidth * length!);
            setCurrentItem(length! - 1);
        } else {
            setScrollByitemWidth(itemRef.current.offsetWidth * scrollBy! * -1);
            setCurrentItem(currentItem - 1);
        }
        event.target.setAttribute("disabled", true);
        setTimeout(function () {
            event.target.removeAttribute("disabled");
        }, 800);
    };

    const handleNextArrow = (event: any) => {
        if (isLastItem) {
            setScrollByitemWidth(itemRef.current.offsetWidth * length! * -1);
            setCurrentItem(0);
        } else {
            setScrollByitemWidth(itemRef.current.offsetWidth * scrollBy!);
            setCurrentItem(currentItem + 1);
        }
        event.target.setAttribute("disabled", true);
        setTimeout(function () {
            event.target.removeAttribute("disabled");
        }, 800);
    };

    const prevCountRef = useRef<any>(undefined);

    useEffect(() => {
        prevCountRef.current = currentItem;
    }, [currentItem]);
    const prevCount = prevCountRef.current;

    useEffect(() => {
        if (itemRef.current)
            if (currentItem > prevCount) {
                setScrollByitemWidth(itemRef.current.offsetWidth * (currentItem - prevCount));
            } else {
                setScrollByitemWidth(itemRef.current.offsetWidth * (prevCount - currentItem) * -1);
            }
    }, [currentItem, itemRef]);

    const onClickDotHandleItems = (targetItem: number) => {
        setCurrentItem(targetItem);
    };

    useEffect(() => {
        let interval: any;
        if (!isMouseOver) {
            interval = setInterval(() => {
                if (currentItem < length! - 1) {
                    setCurrentItem((count) => count + 1);
                } else {
                    setCurrentItem((count) => count - (length! - 1));
                }
            }, 5000);
        } else {
            clearInterval(interval);
        }

        return () => {
            clearInterval(interval);
        };
    }, [currentItem, isMouseOver]);

    const onMouseEnterStopInterval = () => {
        setIsMouseOver(true);
    };
    const onMouseLeaveStartInterval = () => {
        setIsMouseOver(false);
    };

    return (
        <div className={classnames(Styles.main, containerClass)}>
            {(items?.length || children) && (
                <>
                    <div ref={elmRef} className={classnames(Styles.row, rowClass)}>
                        {items?.map((item, index) => (
                            <div
                                key={index}
                                ref={index === 0 ? firstElmRef : index === items.length - 1 ? lastElmRef : itemRef}
                                className={classnames(Styles.item, itemClass, currentItem === index && activeClass)}
                                onMouseEnter={onMouseEnterStopInterval}
                                onMouseLeave={onMouseLeaveStartInterval}
                            >
                                {item}
                            </div>
                        ))}
                        {children}
                    </div>

                    {prvArrow && items && items.length > 1 ? (
                        <button className={classnames(Styles.prev, prvArrowClass)} onClick={(e) => handlePrvArrow(e)}>
                            {prvArrow}
                        </button>
                    ) : (
                        <></>
                    )}

                    {nextArrow && items && items.length > 1 ? (
                        <button className={classnames(Styles.next, nextArrowClass)} onClick={(e) => handleNextArrow(e)}>
                            {nextArrow}
                        </button>
                    ) : (
                        <></>
                    )}
                    {items && items.length > 1 ? (
                        <div className={classnames(Styles.dots, dotsClass)}>
                            {items?.map((data, index) => (
                                <span
                                    key={index}
                                    className={classnames(
                                        Styles.dot,
                                        currentItem === index && Styles.activeDot,
                                        currentItem === index && activeDotClass
                                    )}
                                    onClick={() => onClickDotHandleItems(index)}
                                ></span>
                            ))}
                        </div>
                    ) : (
                        <></>
                    )}
                </>
            )}

            {fallback}
        </div>
    );
};

export default CustomCarousel;
